import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useClerk } from '@clerk/react';
import { X } from 'lucide-react';

type Props = {
  /** Class on the trigger button — match the visual variant of the surrounding button (e.g. "primary lg"). */
  className?: string;
  /** Label/content of the trigger button. */
  children: React.ReactNode;
};

/**
 * Sign-up CTA with an interstitial that warns the user Invitey accounts must
 * belong to an organization. Click the button → modal opens with the
 * explanation + a "Continue" action that opens Clerk's hosted sign-up. The
 * cancel/overlay routes them back without ever invoking Clerk, so they
 * don't burn an account on a flow they didn't mean to start.
 *
 * Replaces direct uses of `<SignUpButton mode="modal">` in the marketing
 * surfaces (top nav + hero + final CTA on Home).
 */
export default function SignUpGateButton({ className, children }: Props) {
  const [open, setOpen] = useState(false);
  const clerk = useClerk();

  function proceed() {
    setOpen(false);
    clerk.openSignUp({ forceRedirectUrl: '/welcome' });
  }

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>

      {/* Rendered in a portal to document.body so the fixed overlay is
          positioned against the viewport, not a transformed/filtered ancestor.
          The topbar has a backdrop-filter, which would otherwise become the
          containing block for this fixed element and clip the modal. */}
      {open &&
        createPortal(
          <div
            className="modal-overlay"
            onClick={() => setOpen(false)}
            role="presentation"
          >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signup-gate-title"
          >
            <button
              type="button"
              className="ghost modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={16} />
            </button>

            <h2 id="signup-gate-title" style={{ marginTop: 0 }}>
              Before you sign up
            </h2>
            <p>
              Signing up here creates a <strong>new organization</strong> for
              your business. Every other Invitey account &mdash; staff,
              teammates, anyone joining an organization that already exists
              &mdash; must be invited by their admin instead.
            </p>
            <p className="muted small" style={{ marginBottom: '1.25rem' }}>
              If your company already uses Invitey, ask them to send you an
              invite link rather than signing up here.
            </p>

            <div className="row" style={{ gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button type="button" className="ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={proceed}>
                Continue to sign up
              </button>
            </div>
          </div>
          </div>,
          document.body,
        )}
    </>
  );
}
